import React from 'react';
import { FileText, Shield, Database, Brain, TrendingUp, Users, CheckCircle } from 'lucide-react';

const Whitepaper = () => {
  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-notaria-primary to-notaria-secondary py-20 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <FileText className="w-20 h-20 mx-auto mb-6 animate-pulse" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Whitepaper: Notaría Pública 90 4.0
            </h1>
            <p className="text-2xl text-cyan-100">
              El Futuro de la Fe Pública: Banking, Blockchain e Inteligencia Artificial
            </p>
          </div>
        </div>
      </section>

      {/* El Pilar Jurídico */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <Shield className="w-12 h-12 text-notaria-primary" />
              <h2 className="text-4xl font-bold text-notaria-primary">
                El Pilar Jurídico: La Autoridad en la Era Digital
              </h2>
            </div>
            
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              La Notaría Pública 90 es la base de confianza sobre la cual se construye este ecosistema. Mientras que la tecnología aporta velocidad y transparencia, la Notaría 90 aporta <strong>certeza jurídica y fe pública</strong>.
            </p>

            {/* Subsections */}
            <div className="space-y-8">
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-notaria-secondary">
                <h3 className="text-2xl font-bold text-notaria-primary mb-4">
                  1. Seguridad Jurídica Integral
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Bajo el liderazgo de su consejo de administración, la Notaría Pública 90 garantiza que cada proceso digital —desde una firma electrónica hasta la tokenización de un activo— cumpla estrictamente con el Marco Legal Mexicano y los estándares internacionales de derecho civil y mercantil.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-notaria-secondary">
                <h3 className="text-2xl font-bold text-notaria-primary mb-4">
                  2. El Notario como Validador de Tecnología
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  En la Notaría 90, el protocolo notarial evoluciona. No solo actuamos sobre documentos físicos, sino que validamos la integridad de los procesos en Hitchain. Nuestra función es asegurar que el "mundo real" y el "mundo digital" estén perfectamente alineados:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-notaria-secondary mt-1 flex-shrink-0" />
                    <span className="text-gray-700"><strong>Validación de Identidad:</strong> Procesos presenciales y remotos (KYC) respaldados por la fe pública.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-notaria-secondary mt-1 flex-shrink-0" />
                    <span className="text-gray-700"><strong>Protocolización de Activos Digitales:</strong> Transformamos contratos tradicionales en estructuras listas para la economía blockchain.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-notaria-secondary">
                <h3 className="text-2xl font-bold text-notaria-primary mb-4">
                  3. Sinergia Estratégica
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  La Notaría Pública 90 actúa como el filtro de calidad y legalidad para las inversiones en Arkangeles. Cada proyecto que busca fondeo a través de crowdfunding de empresas cuenta con una revisión estructural de nuestra parte, asegurando que sea un proyecto legalmente viable y certificable.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-notaria-secondary">
                <h3 className="text-2xl font-bold text-notaria-primary mb-4">
                  4. Transparencia y Gobierno Corporativo
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Operamos bajo un esquema de Gobierno Corporativo moderno. La Notaría 90 no es una oficina unipersonal, sino una institución con un Consejo de Administración que se reúne trimestralmente para auditar procesos, actualizar protocolos de ciberseguridad y garantizar que la innovación tecnológica nunca comprometa la ética legal.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visión General */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-notaria-primary mb-8 text-center">
              1. Visión General: La Evolución de la Confianza
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed text-center mb-8">
              La Notaría Pública 90 trasciende la función tradicional para convertirse en una <strong className="text-notaria-secondary">Notaría 4.0</strong>. En un mundo digitalizado, el notario evoluciona de ser un custodio de papel a un <strong className="text-notaria-secondary">Oráculo de Confianza Digital</strong>. Este Whitepaper detalla la integración de un ecosistema donde la certeza jurídica se encuentra con la velocidad de la tecnología.
            </p>
          </div>
        </div>
      </section>

      {/* El Ecosistema */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-notaria-primary mb-12 text-center">
              2. El Ecosistema de Innovación
            </h2>
            <p className="text-lg text-gray-700 mb-12 text-center max-w-4xl mx-auto">
              Nuestra operatividad se fundamenta en la sinergia de plataformas líderes que garantizan transparencia, agilidad y seguridad:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Notaría 90 */}
              <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                <Shield className="w-12 h-12 text-notaria-primary mb-4" />
                <h3 className="text-2xl font-bold text-notaria-primary mb-3">Notaría 90</h3>
                <p className="text-gray-700">
                  El núcleo jurídico. El punto donde la ley se encuentra con el ciudadano y la empresa.
                </p>
              </div>

              {/* Hitchain */}
              <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                <Database className="w-12 h-12 text-notaria-secondary mb-4" />
                <h3 className="text-2xl font-bold text-notaria-primary mb-3">Hitchain</h3>
                <p className="text-gray-700">
                  Nuestra capa de infraestructura tecnológica. Implementamos protocolos de doble certificación utilizando tecnología blockchain para garantizar que cada documento sea inmutable, rastreable y eterno.
                </p>
              </div>

              {/* Intranot90 */}
              <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                <Users className="w-12 h-12 text-cyan-500 mb-4" />
                <h3 className="text-2xl font-bold text-notaria-primary mb-3">Intranot90</h3>
                <p className="text-gray-700">
                  La columna vertebral operativa. Una intranet de gestión avanzada que permite la trazabilidad total de los procesos notariales en tiempo real.
                </p>
              </div>

              {/* Arkangeles */}
              <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                <TrendingUp className="w-12 h-12 text-green-500 mb-4" />
                <h3 className="text-2xl font-bold text-notaria-primary mb-3">Arkangeles</h3>
                <p className="text-gray-700">
                  La frontera financiera. Una alianza estratégica de equity crowdfunding mexicana que permite la democratización de inversiones en empresas, respaldada por la validación legal y estructural de nuestra notaría.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pilares Tecnológicos */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-notaria-primary mb-12 text-center">
              3. Pilares Tecnológicos (La Tríada 4.0)
            </h2>

            <div className="space-y-8">
              {/* Banking & Fintech */}
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-8 rounded-lg border-l-4 border-green-500">
                <h3 className="text-3xl font-bold text-green-700 mb-4">A. Banking & Fintech</h3>
                <p className="text-gray-800 leading-relaxed">
                  Optimizamos la interacción con el sistema financiero. Facilitamos la validación de procedencia de recursos y la estructuración legal necesaria para la apertura de cuentas y movimientos de capital de alto impacto, reduciendo la fricción entre la ley y la banca.
                </p>
              </div>

              {/* Blockchain & Tokenización */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-8 rounded-lg border-l-4 border-blue-500">
                <h3 className="text-3xl font-bold text-blue-700 mb-4">B. Blockchain & Tokenización</h3>
                <p className="text-gray-800 leading-relaxed mb-4">
                  A través de Hitchain, lideramos la Tokenización Inmobiliaria y de Contratos.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-800"><strong>Certificación Dual:</strong> Cada acto notarial físico tiene un gemelo digital en la cadena de bloques.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-800"><strong>Smart Contracts:</strong> Estructuramos la base legal para contratos inteligentes que se ejecutan automáticamente tras la validación notarial.</span>
                  </li>
                </ul>
              </div>

              {/* IA */}
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-8 rounded-lg border-l-4 border-purple-500">
                <h3 className="text-3xl font-bold text-purple-700 mb-4">C. Inteligencia Artificial (IA)</h3>
                <p className="text-gray-800 leading-relaxed">
                  Utilizamos modelos de IA para la automatización de procesos repetitivos, análisis de cumplimiento normativo y pre-validación de documentos, permitiendo que nuestro equipo humano se enfoque en el asesoramiento estratégico y personalizado.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gobierno Corporativo */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-notaria-primary mb-8">
              4. Gobierno Corporativo y Transparencia
            </h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              La Notaría 4.0 se rige por los más altos estándares de gobernanza institucional:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-notaria-primary mb-3">Consejo de Administración</h3>
                <p className="text-gray-700">
                  Un órgano colegiado que supervisa la dirección estratégica y tecnológica.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-notaria-primary mb-3">Sesiones Trimestrales</h3>
                <p className="text-gray-700">
                  Auditoría y revisión constante de procesos para asegurar la mejora continua y el cumplimiento normativo.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PLD y KYC */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-notaria-primary mb-8">
              5. Prevención de Lavado de Dinero (PLD) y KYC
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              La seguridad es nuestra prioridad. Implementamos protocolos KYC (Know Your Customer) de grado bancario. Cada usuario y empresa dentro de nuestro ecosistema pasa por un filtro de identidad digital avanzado, asegurando que la Notaría 90 sea un entorno seguro y libre de riesgos operativos.
            </p>
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="py-16 bg-gradient-to-br from-notaria-primary to-notaria-secondary text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center">6. Roadmap: El Futuro Próximo</h2>
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
                <div className="flex items-start gap-3">
                  <span className="text-3xl font-bold text-cyan-300">1.</span>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Integración Total con Arkangeles</h3>
                    <p className="text-gray-200">Lanzamiento de proyectos de inversión con certificación notarial automatizada.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
                <div className="flex items-start gap-3">
                  <span className="text-3xl font-bold text-cyan-300">2.</span>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Expansión de Hitchain</h3>
                    <p className="text-gray-200">Desarrollo de una red de confianza distribuida para el resguardo de documentos electrónicos con validez oficial.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
                <div className="flex items-start gap-3">
                  <span className="text-3xl font-bold text-cyan-300">3.</span>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Lanzamiento de Apps Nativas</h3>
                    <p className="text-gray-200">Acceso al ecosistema Notaría 90 desde dispositivos iOS y Android para gestión de firmas y consultas en tiempo real.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conclusión */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Conclusión</h2>
            <p className="text-xl text-gray-300 leading-relaxed">
              La Notaría 4.0 no es solo una actualización de software; es un <strong className="text-cyan-400">cambio de paradigma</strong>. Es la unión de la Notaría 90, Hitchain y Arkangeles para crear un estándar de confianza global, donde lo legal es eficiente y lo tecnológico es seguro.
            </p>
            <div className="mt-8">
              <p className="text-2xl font-bold text-cyan-400">Notaría Pública 90 | Confianza en la era Digital.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Whitepaper;